import Client from "..";
import { Effect } from "../inventory/Effects";
import { GameEvents } from "./Types";

export type OnZoneEnterFunction = (effects?: Effect[]) => void;
export type OnZoneLeaveFunction = (effects?: Effect[]) => void;
export type OnZoneDropFunction = (effects?: Effect[]) => boolean; // If successful

export const enum DropZoneShape {
  CIRCLE = "circle",
  RECTANGLE = "rectangle",
  POLYGON = "polygon",
}

export class ItemDropZone<T> extends Phaser.GameObjects.Zone {
  private shapeClass?: DropZoneShape;
  private shapeArgs?: unknown[];

  constructor(
    scene: Phaser.Scene,
    public supportedDropZoneTypes: T[],
    public OnZoneEnter: OnZoneEnterFunction,
    public OnZoneLeave: OnZoneLeaveFunction,
    public OnZoneDrop: OnZoneDropFunction,
  ) {
    super(scene, 0, 0, 1, 1);

    Client.game.events.on(
      GameEvents.ENABLE_DROP_ZONES,
      this.onEnableDropZone,
      this,
    );
    Client.game.events.on(
      GameEvents.DISABLE_DROP_ZONES,
      this.onDisableDropZone,
      this,
    );
  }

  public SetShape(hitAreaClass: DropZoneShape, ...args: unknown[]): void {
    // Store the shape information for later use
    this.shapeClass = hitAreaClass;
    this.shapeArgs = args;

    if (this.input && this.input.hitArea) {
      switch (hitAreaClass) {
        case DropZoneShape.CIRCLE:
        case DropZoneShape.RECTANGLE:
          this.input.hitArea.setTo(0, 0, ...args);
          break;
        case DropZoneShape.POLYGON:
          this.input.hitArea.setTo(...args);
          break;
      }
    }
    // Don't make it interactive by default - only when drop zones are enabled
    // The shape will be created when onEnableDropZone is called
  }

  public Supports(itemType: T): boolean {
    return this.supportedDropZoneTypes.includes(itemType);
  }

  public override destroy(fromScene?: boolean): void {
    // Unsubscribe from global events to prevent hanging references
    if (Client.game.events) {
      Client.game.events.off(
        GameEvents.ENABLE_DROP_ZONES,
        this.onEnableDropZone,
        this,
      );
      Client.game.events.off(
        GameEvents.DISABLE_DROP_ZONES,
        this.onDisableDropZone,
        this,
      );
    }

    super.destroy(fromScene);
  }

  private onEnableDropZone(itemTypes: T[]): void {
    // Check if object is destroyed
    if (!this.scene) return;
    itemTypes.forEach((type) => {
      if (this.Supports(type)) {
        // Make the zone interactive when enabled
        if (!this.input && this.shapeClass && this.shapeArgs) {
          this.createShape(this.shapeClass, ...this.shapeArgs);
        }
        if (this.input) {
          this.input.dropZone = true;
        }
        return;
      }
    });
  }

  private onDisableDropZone(itemTypes?: T[]): void {
    // Check if object is destroyed
    if (!this.scene) return;

    if (itemTypes) {
      itemTypes.forEach((type) => {
        if (this.Supports(type)) {
          if (this.input) {
            this.input.dropZone = false;
            this.removeInteractive();
          }
          return;
        }
      });
    } else if (this.input) {
      this.input.dropZone = false;
      this.removeInteractive();
    }
  }

  private createShape(hitAreaClass: DropZoneShape, ...args: unknown[]): void {
    let shape;
    let shapeCallback;
    switch (hitAreaClass) {
      case DropZoneShape.CIRCLE:
        shape = new Phaser.Geom.Circle(0, 0, ...(args as number[]));
        shapeCallback = Phaser.Geom.Circle.Contains;
        break;
      case DropZoneShape.RECTANGLE:
        shape = new Phaser.Geom.Rectangle(0, 0, ...(args as number[]));
        shapeCallback = Phaser.Geom.Rectangle.Contains;
        break;
      case DropZoneShape.POLYGON:
        shape = new Phaser.Geom.Polygon(...(args as number[][]));
        shapeCallback = Phaser.Geom.Polygon.Contains;
        break;
    }
    this.setInteractive(shape, shapeCallback);
  }
}
